import { Table, type TableColumnsType } from 'antd';
import { type ReactNode } from 'react';

import { useMediaQuery } from './useMediaQuery';

/**
 * A list, as a table on a desk and as cards on a phone — from one column definition.
 *
 * The problem it solves is specific to this template. `style.css` puts `text-nowrap` on every
 * `td` and `th`, and the list screens pass `scroll={{ x: 'max-content' }}`, so a table never
 * reflows: below about 640px it becomes a horizontally scrolling strip inside a card, showing the
 * first column and hiding everything that makes the row worth reading. That is fine on a laptop
 * and unusable on a counter phone.
 *
 * The alternative — a `renderCard` prop per screen — was rejected because it makes every list
 * screen carry two presentations that drift apart, and the second one is the one nobody looks at.
 * Here the cards are built from the *same* `columns` array: same `title` for the label, same
 * `render` for the value, so a column added for the table appears in the card for free and a
 * formatting fix lands in both.
 *
 * Three flags decide how a column behaves once it is a card, and they are the whole API:
 * `primary` is the heading, `trailing` sits in the top-right (the actions column), `hideOnCard`
 * drops out. Everything else becomes a labelled row.
 */
export type DataViewColumn<T> = TableColumnsType<T>[number] & {
  /**
   * The card's heading — rendered wide and without a label. Set it on the column a person would
   * read first to identify the row; exactly one column should have it.
   */
  primary?: boolean;

  /**
   * Rendered in the card's top-right rather than as a labelled row. Meant for the actions column,
   * whose `title` is empty and whose buttons read as chrome rather than as a value.
   */
  trailing?: boolean;

  /**
   * Left out of the card entirely. For a column that only earns its width beside the others — a
   * duplicate of something already in the heading, or a value too long to be worth the row.
   */
  hideOnCard?: boolean;
};

/** Below this, cards. `sm` in Tailwind's scale, expressed exclusively so 640px itself is a table. */
const cardsBelow = '(max-width: 639.98px)';

export function DataView<T extends object>({
  columns,
  dataSource,
  loading = false,
  empty,
  rowKey = 'id',
  selectedKeys,
  onSelectionChange,
}: {
  columns: DataViewColumn<T>[];
  dataSource: T[];
  loading?: boolean;
  /** Shown when there is nothing to list. The same node serves both layouts. */
  empty?: ReactNode;
  rowKey?: string;

  /**
   * Rows the caller considers picked. Opt-in: omit both of these and the view is exactly what it
   * was, which is what keeps every existing list screen untouched.
   *
   * <p>
   * Added for Section 10's batch dispatch — "send 40 units at once" — and put here rather than in
   * that screen because a checkbox column bolted on by one caller would render as a column on a
   * desk and as nothing at all on a phone, which is precisely the drift `DataView` exists to stop.
   * </p>
   */
  selectedKeys?: number[];
  onSelectionChange?: (keys: number[]) => void;
}) {
  const cards = useMediaQuery(cardsBelow);
  const selectable = selectedKeys !== undefined && onSelectionChange !== undefined;

  if (cards) {
    return (
      <CardList
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        empty={empty}
        rowKey={rowKey}
        {...(selectable ? { selectedKeys, onSelectionChange } : {})}
      />
    );
  }

  return (
    <Table<T>
      rowKey={rowKey}
      // The card-only flags are ours and antd has never heard of them. Passed through as-is:
      // they are extra keys on a config object it never reads, and stripping them would mean
      // rebuilding the whole array on every render to remove three properties.
      columns={columns}
      dataSource={dataSource}
      loading={loading}
      pagination={false}
      size="middle"
      scroll={{ x: 'max-content' }}
      {...(selectable
        ? {
            rowSelection: {
              selectedRowKeys: selectedKeys,
              onChange: (keys: React.Key[]) => onSelectionChange(keys.map(Number)),
            },
          }
        : {})}
      {...(empty ? { locale: { emptyText: empty } } : {})}
    />
  );
}

function CardList<T extends object>({
  columns,
  dataSource,
  loading,
  empty,
  rowKey,
  selectedKeys,
  onSelectionChange,
}: {
  columns: DataViewColumn<T>[];
  dataSource: T[];
  loading: boolean;
  empty?: ReactNode;
  rowKey: string;
  selectedKeys?: number[];
  onSelectionChange?: (keys: number[]) => void;
}) {
  if (loading && dataSource.length === 0) {
    return <p className="py-10 text-center text-sm text-default">Loading…</p>;
  }

  if (dataSource.length === 0) {
    return <div className="py-6">{empty}</div>;
  }

  const primary = columns.find((column) => column.primary);
  const trailing = columns.find((column) => column.trailing);
  const rest = columns.filter(
    (column) => !column.primary && !column.trailing && !column.hideOnCard,
  );

  return (
    <div
      // `divide-y` rather than a gap and a border each: the cards are a list, and stacked panels
      // separated by one hairline read as one control rather than as a scatter of boxes.
      className="divide-y divide-border-color"
      // The table it replaces is a grid to a screen reader; so is this.
      role="list"
    >
      {dataSource.map((row, index) => {
        const key = Number(keyOf(row, rowKey, index));
        const picked = selectedKeys?.includes(key) ?? false;

        return (
          <div key={String(keyOf(row, rowKey, index))} role="listitem" className="py-3">
            <div className="flex items-start justify-between gap-3">
              {/* The checkbox rides beside the heading rather than in the values grid below: it is
                the row's control, not one of its facts, and a `dd` is the wrong place for it. */}
              {onSelectionChange && (
                <input
                  type="checkbox"
                  className="mt-1 shrink-0"
                  aria-label="Select"
                  checked={picked}
                  onChange={() =>
                    onSelectionChange(
                      picked
                        ? (selectedKeys ?? []).filter((k) => k !== key)
                        : [...(selectedKeys ?? []), key],
                    )
                  }
                />
              )}
              <div className="min-w-0 flex-1">{primary && cell(primary, row, index)}</div>
              {trailing && <div className="shrink-0">{cell(trailing, row, index)}</div>}
            </div>

            {rest.length > 0 && (
              <dl
                // **Two columns only when there are two things to put in them.** A single value in a
                // `grid-cols-2` takes half the card and leaves the other half blank — measured at
                // 161px of 334px on a 360px phone, with three role badges wrapping onto three lines
                // beside an empty column. Both class names are written out because Tailwind scans
                // source text: a computed `grid-cols-${n}` generates neither and the grid silently
                // falls back to one column everywhere.
                className={`mt-2 grid gap-x-3 gap-y-1.5 ${
                  rest.length === 1 ? 'grid-cols-1' : 'grid-cols-2'
                }`}
              >
                {rest.map((column, columnIndex) => (
                  <div key={columnKey(column, columnIndex)} className="min-w-0">
                    {/*
                      Sized in `--scale-*`, not in literals. `text-[11px]` and `text-[13px]` consume
                      neither an antd token nor a custom property, so the whole card presentation
                      ignored the person's text-size setting — the desk table followed it and the
                      phone did not. Compact reproduces the two literals exactly, which is what that
                      step of the scale is for.
                    */}
                    <dt className="text-[length:var(--scale-list-meta)] tracking-wide text-default uppercase">
                      {column.title as ReactNode}
                    </dt>
                    <dd className="truncate text-[length:var(--scale-font-size)] text-gray-900">
                      {cell(column, row, index)}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * One cell, rendered exactly as the table would render it.
 *
 * antd's contract is `render(value, record, index)` with `value` picked out by `dataIndex`, so
 * that is reproduced rather than approximated — a column whose `render` ignores its first argument
 * (most of them) behaves identically, and one that uses it still gets the right value.
 */
function cell<T extends object>(column: DataViewColumn<T>, row: T, index: number): ReactNode {
  const dataIndex = 'dataIndex' in column ? column.dataIndex : undefined;

  const value =
    typeof dataIndex === 'string' ? (row as Record<string, unknown>)[dataIndex] : undefined;

  const render = 'render' in column ? column.render : undefined;

  if (typeof render === 'function') {
    return render(value, row, index) as ReactNode;
  }

  return value as ReactNode;
}

function columnKey<T extends object>(column: DataViewColumn<T>, index: number): string {
  if ('key' in column && (typeof column.key === 'string' || typeof column.key === 'number')) {
    return String(column.key);
  }

  if ('dataIndex' in column && typeof column.dataIndex === 'string') {
    return column.dataIndex;
  }

  return String(index);
}

function keyOf<T extends object>(row: T, rowKey: string, index: number): unknown {
  const value = (row as Record<string, unknown>)[rowKey];

  return value ?? index;
}
